import { useEffect, useRef, useState } from 'react';
import maplibregl, { Map as MlMap, Marker } from 'maplibre-gl';
import { circle } from '@turf/turf';
import type { Feature, FeatureCollection, LineString } from 'geojson';
import { COLORS, ORIGIN } from '../../shared/constants';
import type { PublicState, LngLat } from '../../shared/protocol';
import { MAP_STYLE_URL } from './style';
import { hiderPinEl, portalExitPinEl, seekerPinEl, thermoPinEl } from './pins';
import { boxAround } from './roads';
import type { CutPreview } from './cutPreview';

const PORTAL_GREEN = COLORS.portal;

const EMPTY: FeatureCollection = { type: 'FeatureCollection', features: [] };

function makeHatch(map: MlMap) {
  if (map.hasImage('moss-hatch')) return;
  const s = 8;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const ctx = c.getContext('2d')!;
  ctx.strokeStyle = COLORS.moss;
  ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(0, s);
  ctx.lineTo(s, 0);
  ctx.moveTo(-2, 2);
  ctx.lineTo(2, -2);
  ctx.moveTo(s - 2, s + 2);
  ctx.lineTo(s + 2, s - 2);
  ctx.stroke();
  map.addImage('moss-hatch', { width: s, height: s, data: ctx.getImageData(0, 0, s, s).data });
}

/** Renders the branded base map + the full game overlay stack from PublicState.
 *  Privacy is upstream: a seeker's `state` never contains hider geometry, so
 *  this component cannot render what it never receives. */
/** v18 BOLT-080 — every layer THIS component installs. Flying Blind hides the
 *  style's layers and keeps these, so the seeker still sees the play area, the
 *  zone, their own pin and any live cut preview: a navigation tax, not a
 *  blackout of the game state. Keep in sync with the addLayer block below —
 *  `mapview-overlays.test.ts` asserts the two match, mechanically. */
export const OVERLAY_IDS = new Set([
  'eliminated-fill',
  'eliminated-hatch',
  'eliminated-line',
  'zone-fill',
  'zone-line',
  'freeze-fill', // v20 BOLT-097 — the Freeze lock ring (seeker only)
  'freeze-line',
  'radar-ping-fill',
  'radar-ping-line',
  'cut-a-fill',
  'cut-b-fill',
  'cut-b-line',
  'cut-lines-layer',
  'portal-exit-fill',
  'portal-exit-line',
  'portal-entry-fill',
  'portal-entry-line',
  'hiding-fill',
  'hiding-line',
  'boundary-glow',
  'boundary-line',
  'thermo-line-layer',
]);

/** v18 BOLT-080 — is the seeker's map dark right now? Pure, shared with iOS's
 *  port (Format.mapIsDark) and unit-tested: only the SEEKER, only while the
 *  curse runs. The hider's map never goes dark (they need to hide, not seek). */
export function mapIsDark(state: PublicState | null): boolean {
  return state?.you.role === 'seeker' && state.activeCurse?.kind === 'flyingBlind';
}

export function MapView({
  state,
  preview = null,
  interactive = true,
  fitOnFirstBoundary = true,
  refitBoundary = false,
  onMapClick,
}: {
  state: PublicState | null;
  /** v11 §2G: the live cut preview (candidate sections + dashed cut lines). */
  preview?: CutPreview | null;
  interactive?: boolean;
  fitOnFirstBoundary?: boolean;
  /** v27 BOLT-150: the lobby PREVIEW re-frames every time the area changes,
   *  because the host is switching modes and editing regions and needs to see
   *  the shape they just made rather than a corner of it. */
  refitBoundary?: boolean;
  onMapClick?: (p: LngLat) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MlMap | null>(null);
  const fitted = useRef(false);
  const lastFit = useRef<unknown>(null);
  const portalFitted = useRef(false);
  // marker store: fixed pins + one exit beacon per portal (portalExit0…N — v11 §2B)
  const markers = useRef<Partial<Record<string, Marker>>>({});
  const [loaded, setLoaded] = useState(false);
  /** v18 BOLT-080: the style's own layers (the basemap) — hidden while Flying
   *  Blind runs, restored when it clears. Filled at load from the live style. */
  const basemapIds = useRef<string[]>([]);
  const lastPing = useRef<string | null>(null);
  const pingTimer = useRef<ReturnType<typeof setTimeout>>();
  const clickCb = useRef(onMapClick);
  clickCb.current = onMapClick;

  // init once
  useEffect(() => {
    if (!ref.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: ref.current,
      style: MAP_STYLE_URL, // v9 BOLT-020: server-served, one source for all clients
      center: [ORIGIN.lng, ORIGIN.lat],
      zoom: 15.4,
      // The preview must hold a 10 sq mi car map (acceptance 8): a floor of 13
      // clamps the fit and the area spills off screen, so the preview gets its
      // own floor. The play map keeps 13 - zooming out past the area mid-game
      // is not a view anyone needs.
      minZoom: refitBoundary ? 9 : 13,
      maxZoom: 19,
      attributionControl: { compact: true },
      interactive,
      fadeDuration: 120,
    });
    mapRef.current = map;
    // E2E hook: expose the live map so tests can read its viewport (getBounds).
    if (typeof window !== 'undefined') (window as unknown as { __boltMap?: MlMap }).__boltMap = map;
    if (interactive)
      map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    // Simulator: a tap sets the player's position (when a handler is wired).
    map.on('click', (e) => clickCb.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng }));

    map.on('load', () => {
      makeHatch(map);
      const add = (id: string) => map.addSource(id, { type: 'geojson', data: EMPTY });
      add('eliminated');
      add('zone');
      add('boundary');
      add('hiding');
      add('thermo-line');
      add('radar-ping');
      add('portal-entry');
      add('portal-exit');
      // v11 §2G: the live cut preview — sections alternate between two
      // hazard-family tints (deliberately non-committal about in/out)
      add('cut-a');
      add('cut-b');
      add('cut-lines');
      add('freeze'); // v20 BOLT-097 — Freeze lock ring

      // stack: eliminated → zone → boundary (per §4.5)
      map.addLayer({ id: 'eliminated-fill', type: 'fill', source: 'eliminated', paint: { 'fill-color': COLORS.moss, 'fill-opacity': 0.16 } });
      map.addLayer({ id: 'eliminated-hatch', type: 'fill', source: 'eliminated', paint: { 'fill-pattern': 'moss-hatch', 'fill-opacity': 0.8 } });
      map.addLayer({ id: 'eliminated-line', type: 'line', source: 'eliminated', paint: { 'line-color': COLORS.moss, 'line-width': 1.3, 'line-dasharray': [4, 3], 'line-opacity': 0.7 } });
      map.addLayer({ id: 'zone-fill', type: 'fill', source: 'zone', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.2 } });
      map.addLayer({ id: 'zone-line', type: 'line', source: 'zone', paint: { 'line-color': COLORS.signal, 'line-width': 2 } });
      // transient radar disk (flashed on resolution)
      map.addLayer({ id: 'radar-ping-fill', type: 'fill', source: 'radar-ping', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.07 } });
      map.addLayer({ id: 'radar-ping-line', type: 'line', source: 'radar-ping', paint: { 'line-color': COLORS.signal, 'line-width': 2, 'line-dasharray': [2, 2], 'line-opacity': 0.9 } });
      // v11 §2G: cut preview — hazard tints (distinct from zone/eliminated),
      // alternating per section; dashed candidate cut lines above them.
      map.addLayer({ id: 'cut-a-fill', type: 'fill', source: 'cut-a', paint: { 'fill-color': COLORS.hazard, 'fill-opacity': 0.16 } });
      map.addLayer({ id: 'cut-b-fill', type: 'fill', source: 'cut-b', paint: { 'fill-color': COLORS.hazard, 'fill-opacity': 0.06 } });
      map.addLayer({ id: 'cut-b-line', type: 'line', source: 'cut-b', paint: { 'line-color': COLORS.hazard, 'line-width': 1, 'line-opacity': 0.4 } });
      map.addLayer({ id: 'cut-lines-layer', type: 'line', source: 'cut-lines', paint: { 'line-color': COLORS.hazard, 'line-width': 2.2, 'line-dasharray': [3, 2.2], 'line-opacity': 0.95 } });
      // Curse of the Portal — entry (red) + exit (green) boxes
      map.addLayer({ id: 'portal-exit-fill', type: 'fill', source: 'portal-exit', paint: { 'fill-color': PORTAL_GREEN, 'fill-opacity': 0.22 } });
      map.addLayer({ id: 'portal-exit-line', type: 'line', source: 'portal-exit', paint: { 'line-color': PORTAL_GREEN, 'line-width': 2.5 } });
      map.addLayer({ id: 'portal-entry-fill', type: 'fill', source: 'portal-entry', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.22 } });
      map.addLayer({ id: 'portal-entry-line', type: 'line', source: 'portal-entry', paint: { 'line-color': COLORS.signal, 'line-width': 2.5 } });
      map.addLayer({ id: 'hiding-fill', type: 'fill', source: 'hiding', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.1 } });
      map.addLayer({ id: 'hiding-line', type: 'line', source: 'hiding', paint: { 'line-color': COLORS.signal, 'line-width': 1.5, 'line-dasharray': [3, 3], 'line-opacity': 0.55 } });
      map.addLayer({ id: 'boundary-glow', type: 'line', source: 'boundary', paint: { 'line-color': COLORS.bone, 'line-width': 9, 'line-opacity': 0.08, 'line-blur': 4 } });
      map.addLayer({ id: 'boundary-line', type: 'line', source: 'boundary', paint: { 'line-color': COLORS.bone, 'line-width': 2.2, 'line-opacity': 0.6, 'line-dasharray': [4, 2.5] } });
      map.addLayer({ id: 'thermo-line-layer', type: 'line', source: 'thermo-line', paint: { 'line-color': COLORS.bone, 'line-width': 1.5, 'line-dasharray': [2, 2], 'line-opacity': 0.6 } });
      // v20 BOLT-097 — the Freeze lock ring: stay in / return to this circle.
      map.addLayer({ id: 'freeze-fill', type: 'fill', source: 'freeze', paint: { 'fill-color': COLORS.signal, 'fill-opacity': 0.1 } });
      map.addLayer({ id: 'freeze-line', type: 'line', source: 'freeze', paint: { 'line-color': COLORS.signal, 'line-width': 2.5, 'line-dasharray': [2, 2] } });

      // v18 BOLT-080 (Flying Blind): everything the STYLE brought is basemap;
      // everything we just added is overlay. Deriving the split here means a
      // style edit can never silently un-blind the card, and no id list has to
      // be maintained by hand. 'bg' stays lit — the slate ground is the app's
      // own background, not information.
      basemapIds.current = map
        .getStyle()
        .layers.map((l) => l.id)
        .filter((id) => id !== 'bg' && !OVERLAY_IDS.has(id));
      setLoaded(true);
      map.once('idle', () => ref.current?.setAttribute('data-map-ready', 'true'));
    });

    return () => {
      const w = window as unknown as { __boltMap?: MlMap };
      if (w.__boltMap === map) delete w.__boltMap;
      map.remove();
      mapRef.current = null;
      fitted.current = false;
      portalFitted.current = false;
      markers.current = {};
      setLoaded(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v18 BOLT-080 (Flying Blind): the basemap's VISIBILITY, re-applied on every
  // state change — so a style reload, a rotation, or a rejoin mid-curse all end
  // up in the right place without a special case.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const dark = mapIsDark(state);
    for (const id of basemapIds.current) {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', dark ? 'none' : 'visible');
    }
    // E2E/debug hook: the state is readable without pixel-diffing tiles.
    ref.current?.setAttribute('data-map-dark', dark ? 'true' : 'false');
  }, [state, loaded]);

  // sync overlays whenever state changes OR the map finishes loading
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !loaded) return;
    const s = state;
    setData(map, 'boundary', s?.boundary ?? null);
    setData(map, 'zone', s?.zone ?? null);
    setData(map, 'eliminated', s?.eliminated ?? null);
    setData(map, 'hiding', s?.hidingZone ?? null);
    // v20 BOLT-097 — the Freeze lock ring (seeker-only; freezeZone is absent otherwise).
    const fz = s?.freezeZone ?? null;
    setData(
      map,
      'freeze',
      fz ? circle([fz.anchor.lng, fz.anchor.lat], fz.radiusFt * 0.3048, { units: 'meters', steps: 48 }) : null,
    );

    const thermo = s?.thermo ?? null;
    placeMarker(map, markers.current, 'thermo', thermo?.startPin ?? null, thermoPinEl);
    if (thermo && s?.seekerPin) {
      const line: Feature<LineString> = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [thermo.startPin.lng, thermo.startPin.lat],
            [s.seekerPin.lng, s.seekerPin.lat],
          ],
        },
      };
      setData(map, 'thermo-line', line);
    } else {
      setData(map, 'thermo-line', null);
    }

    placeMarker(map, markers.current, 'seeker', s?.seekerPin ?? null, seekerPinEl);
    const isHider = s?.you.role === 'hider';
    placeMarker(map, markers.current, 'hider', isHider ? (s?.selfPin ?? null) : null, hiderPinEl);

    // portal boxes (already role-filtered server-side: hider always, seeker once
    // triggered). v11 §2B: N portals render as N entry/exit pairs — `portals`
    // when the server sends it, else the legacy single `portal`.
    const portal = s?.portal ?? null;
    const portals = s?.portals ?? (portal ? [portal] : []);
    setData(map, 'portal-entry', portals.length ? fc(portals.map((p) => boxAround(p.entry, p.sizeFt))) : null);
    setData(map, 'portal-exit', portals.length ? fc(portals.map((p) => boxAround(p.exit, p.sizeFt))) : null);
    // A fixed-size green beacon at each exit so the small (50 ft) boxes read as
    // destinations from across the play area (role-filtered upstream: seeker
    // only sees a portal once transported).
    for (let i = 0; i < 8; i++) {
      placeMarker(map, markers.current, `portalExit${i}`, portals[i]?.exit ?? null, portalExitPinEl);
    }

    // When the seeker is transported, frame their pin + the exit box so the green
    // exit is on-screen. They've usually zoomed in to navigate, which leaves the
    // far-off exit outside the viewport — fit once per trigger; resolve resets it.
    if (portal?.state === 'triggered' && s?.you.role === 'seeker' && s.seekerPin) {
      if (!portalFitted.current) {
        const b = new maplibregl.LngLatBounds();
        b.extend([s.seekerPin.lng, s.seekerPin.lat]);
        for (const [lng, lat] of boxAround(portal.exit, portal.sizeFt).geometry.coordinates[0]) {
          b.extend([lng, lat]);
        }
        // Pad past the top HUD/banner stack and the bottom card dock so neither
        // the seeker pin nor the exit beacon lands behind chrome.
        map.fitBounds(b, {
          padding: { top: 250, bottom: 200, left: 48, right: 48 },
          duration: 700,
          maxZoom: 16.5,
        });
        portalFitted.current = true;
      }
    } else if (!portal) {
      portalFitted.current = false;
    }

    // v11 §2G: the cut preview layers (already zone-clipped upstream)
    setData(map, 'cut-a', preview ? fc(preview.sections.filter((_, i) => i % 2 === 0)) : null);
    setData(map, 'cut-b', preview ? fc(preview.sections.filter((_, i) => i % 2 === 1)) : null);
    setData(map, 'cut-lines', preview ? fc(preview.lines) : null);

    // Keyed to the boundary OBJECT, so an unchanged area does not re-frame on
    // every position frame and fight a host who panned.
    const refit = refitBoundary && s?.boundary && lastFit.current !== s.boundary;
    if (s?.boundary && (refit || (fitOnFirstBoundary && !fitted.current))) {
      try {
        map.fitBounds(bbox(s.boundary), { padding: 44, duration: 600, maxZoom: 17 });
        fitted.current = true;
        lastFit.current = s.boundary;
      } catch {
        /* ignore */
      }
    }
  }, [state, preview, loaded, fitOnFirstBoundary, refitBoundary]);

  // flash the radar disk for ~3.5s when a new radar resolves
  useEffect(() => {
    const map = mapRef.current;
    const ping = state?.radarPing;
    if (!map || !loaded || !ping || ping.id === lastPing.current) return;
    lastPing.current = ping.id;
    const src = map.getSource('radar-ping') as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(circle([ping.lng, ping.lat], ping.radiusM, { units: 'meters', steps: 64 }));
    clearTimeout(pingTimer.current);
    pingTimer.current = setTimeout(() => {
      (map.getSource('radar-ping') as maplibregl.GeoJSONSource | undefined)?.setData(EMPTY);
    }, 3500);
  }, [state?.radarPing?.id, loaded]);

  return <div ref={ref} className="map-full" data-testid="map" />;
}

/** Wrap features for a multi-portal source (v11 §2B: N boxes in one layer). */
function fc(features: Feature[]): FeatureCollection {
  return { type: 'FeatureCollection', features };
}

function setData(map: MlMap, id: string, data: Feature | FeatureCollection | null) {
  const src = map.getSource(id) as maplibregl.GeoJSONSource | undefined;
  if (src) src.setData(data ?? EMPTY);
}

function placeMarker(
  map: MlMap,
  store: Partial<Record<string, Marker>>,
  key: 'seeker' | 'hider' | 'thermo' | `portalExit${number}`,
  at: LngLat | null,
  make: () => HTMLDivElement,
) {
  const cur = store[key];
  if (!at) {
    cur?.remove();
    store[key] = undefined;
    return;
  }
  if (cur) cur.setLngLat([at.lng, at.lat]);
  else store[key] = new maplibregl.Marker({ element: make(), anchor: 'center' }).setLngLat([at.lng, at.lat]).addTo(map);
}

function bbox(f: Feature): [number, number, number, number] {
  let minX = 180,
    minY = 90,
    maxX = -180,
    maxY = -90;
  const walk = (coords: any) => {
    if (typeof coords[0] === 'number') {
      minX = Math.min(minX, coords[0]);
      maxX = Math.max(maxX, coords[0]);
      minY = Math.min(minY, coords[1]);
      maxY = Math.max(maxY, coords[1]);
    } else coords.forEach(walk);
  };
  walk((f.geometry as any).coordinates);
  return [minX, minY, maxX, maxY];
}
